import type { OfficeGrade, Role } from '@/features/auth/api';
import { apiFetch } from '@/lib/api/http';

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  office_grade: OfficeGrade | null;
  status: 'pending' | 'accepted' | 'revoked' | 'expired';
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  display_name: string | null;
  company: string | null;
  position: string | null;
}

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
}

export function fetchInvitations(page = 1, perPage = 20): Promise<Page<Invitation>> {
  return apiFetch<Page<Invitation>>(`/invitations?page=${page}&per_page=${perPage}`);
}

export interface InvitationCreated {
  invitation: Invitation;
  accept_url: string;
  token: string;
  /** 実際にメールを送ったか。false の間は受諾リンクを直接伝える（D-153） */
  mail_sent: boolean;
}

export function createInvitation(input: {
  email: string;
  role: Role;
  office_grade?: OfficeGrade | null;
  // 招待の時点で分かっているプロフィールの下書き（会員登録と同じ考え方 / D-142）
  display_name?: string | null;
  company?: string | null;
  position?: string | null;
  phone?: string | null;
  contact_email?: string | null;
  bio?: string | null;
}): Promise<InvitationCreated> {
  return apiFetch<InvitationCreated>('/invitations', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
}

export function revokeInvitation(id: string): Promise<void> {
  return apiFetch<void>(`/invitations/${id}`, { method: 'DELETE' });
}

/**
 * 受諾リンクを作り直す（D-153）。
 * トークンはハッシュ保存のため一度閉じたURLは復元できない。「URLを教えて」への手段。
 * 作り直すと古いリンクは無効になる。
 */
export function reissueInvitation(id: string): Promise<InvitationCreated> {
  return apiFetch<InvitationCreated>(`/invitations/${id}/reissue`, { method: 'POST' });
}

/** 招待メールの送信前確認（D-153） */
export interface InvitationMailPreview {
  to: string;
  subject: string;
  body: string;
  /** SMTPが有効か。false の間は記録だけ残して実際には送らない */
  will_send: boolean;
}

export function fetchInvitationMailPreview(
  email: string,
  expiresInDays = 14,
): Promise<InvitationMailPreview> {
  const params = new URLSearchParams({
    email,
    expires_in_days: String(expiresInDays),
  });
  return apiFetch<InvitationMailPreview>(`/invitations/mail-preview?${params.toString()}`);
}
